/* @jsx h */
import {
  computed,
  defineComponent,
  h,
  onMounted,
  onBeforeUnmount,
  ref,
} from "vue";
import { useLocalization, QRCodeComponent } from "@lemmonhq/uikit";
import type { Place } from "./placeTypes";
import { WalletCard } from "./WalletCard";
import { MembershipWalletCard } from "./MembershipWalletCard";
import { WalletBadges } from "./WalletBadges";
import { WalletCardStack } from "./WalletCardStack";
import { passPlatformEnabled } from "./passDesign";

/**
 * A place's cards, opened out of the deck and browsed the same way the deck is —
 * a horizontal swipe stack.
 *
 * Both levels use one gesture: swipe sideways through the places, open a place,
 * swipe sideways through its cards. So the fan is the very same WalletCardStack
 * the deck uses, now holding this place's passes rather than one card per place.
 * A coin card turns over to its till QR; a membership shows its add-to-Wallet
 * face. Full size, because the stack fills the height exactly as it does in the
 * deck.
 *
 * The open/close transition — the fan growing out of (and shrinking back into)
 * the tapped card — lives in the parent's <Transition>; this component is just
 * the header and the stack.
 */

export type PlaceFanProps = {
  place: Place;
  /** Pass key → pass poster image, so every card matches the deck. */
  images: Record<string, string>;
  /** Card to open on — the deep-linked membership, when there is one. */
  initialFocusKey?: string | null;
  onClose: () => void;
};

export const PlaceFan = defineComponent<PlaceFanProps>({
  name: "PlaceFan",
  props: ["place", "images", "initialFocusKey", "onClose"],
  setup(props) {
    const { t } = useLocalization();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    onMounted(() => window.addEventListener("keydown", onKey));
    onBeforeUnmount(() => window.removeEventListener("keydown", onKey));

    const imageFor = (passKey: string) => props.images[passKey] ?? null;
    const frontKey = ref(props.initialFocusKey ?? props.place.items[0]?.key);
    const frontItem = computed(
      () =>
        props.place.items.find((item) => item.key === frontKey.value) ??
        props.place.items[0] ??
        null,
    );
    const frontWallet = computed(() => {
      const item = frontItem.value;
      if (!item) return null;
      return item.kind === "coin" ? item.walletPass : item.pass;
    });

    // The place's cards as stack items — the same shapes the deck builds, so a
    // coin and membership cards both keep their flip-to-QR interaction.
    // Each card's top names its programme (the place is named in the header).
    // Built once; each card face reads its own program's design.
    const deck = props.place.items.map((item) => {
      if (item.kind === "membership") {
        return {
          key: item.key,
          render: () => (
            <MembershipWalletCard
              pass={item.pass}
              eyebrow={item.pass.program_name}
              wide={true}
            />
          ),
          renderBack: () => (
            <MembershipWalletCard
              pass={item.pass}
              eyebrow={item.pass.program_name}
              wide={true}
              largeQr={true}
            />
          ),
        };
      }
      const qr = `lemmon-loyalty:${item.shop.profile_id}`;
      return {
        key: item.key,
        render: () => (
          <WalletCard
            program={item.program}
            shopName={item.program.name}
            wide={true}
            qrValue={qr}
          />
        ),
        renderBack: () => (
          <WalletCard
            program={item.program}
            shopName={item.program.name}
            wide={true}
            back={true}
            center={() => (
              <div class="bg-white rounded-xl p-3 w-full max-w-[12rem] shadow-lg">
                <div class="w-full aspect-square">
                  <QRCodeComponent url={qr} />
                </div>
              </div>
            )}
          />
        ),
      };
    });

    return () => (
      <div class="fixed inset-0 z-50" role="dialog" aria-modal="true">
        <div
          data-fan-backdrop
          class="absolute inset-0 bg-base-100/85 backdrop-blur-md"
          onClick={() => props.onClose()}
        />

        <div data-fan-content class="relative h-full flex flex-col">
          <div class="shrink-0 flex items-center gap-3 px-4 pt-4 pb-2">
            <button
              type="button"
              class="btn btn-ghost btn-sm btn-circle text-base-content"
              aria-label={t("customer.collected.back_to_cards")}
              title={t("customer.collected.back_to_cards")}
              onClick={() => props.onClose()}
            >
              <span class="material-symbols-outlined">arrow_back</span>
            </button>
            <div class="min-w-0">
              <p class="font-semibold text-base-content truncate leading-tight">
                {props.place.name}
              </p>
              <p class="text-[11px] uppercase tracking-wider text-base-content/50 font-mono">
                {props.place.items.length} {t("customer.collected.cards_count")}
              </p>
            </div>
          </div>

          {/* The same swipe stack the deck uses — horizontal browse, full-size
              cards that fill the height. */}
          <div class="flex min-h-0 w-full max-w-2xl flex-1 flex-col gap-2 px-4 pb-5 mx-auto">
            <div class="min-h-0 flex-1">
              <WalletCardStack
                fill={true}
                maxCardWidth={460}
                items={deck}
                focusKey={props.initialFocusKey}
                onFrontChange={(key: string) => (frontKey.value = key)}
              />
            </div>
            {frontItem.value && (
              <div class="flex min-h-7 shrink-0 justify-center">
                <WalletBadges
                  compact={true}
                  appleUrl={
                    !frontWallet.value ||
                    !passPlatformEnabled(
                      frontWallet.value.kind,
                      frontWallet.value.design,
                      "apple",
                    )
                      ? null
                      : `/api/v3/customer/wallet/pass/${frontWallet.value.serial}.pkpass`
                  }
                  googleUrl={
                    !frontWallet.value ||
                    !passPlatformEnabled(
                      frontWallet.value.kind,
                      frontWallet.value.design,
                      "google",
                    )
                      ? null
                      : (frontWallet.value.google_save_url ??
                        `/api/v3/customer/wallet/google/${frontWallet.value.serial}`)
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
});
